import { useRef, useEffect, FC, KeyboardEvent } from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { ArrowUp } from 'lucide-react';

import { useSocietyAiAgentPage } from '../context/SocietyAiAgentPageContext';

import ApiSelector from './ApiSelector';

type FloatingComposerProps = {
  placeholder?: string;
  autoFocus?: boolean;
};

const MAX_HEIGHT = 200;

const FloatingComposer: FC<FloatingComposerProps> = ({
  placeholder = 'CHATBOT_InputPlaceholder',
  autoFocus = false,
}) => {
  const { t } = useTranslate();
  const { data, actions } = useSocietyAiAgentPage();

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (autoFocus) textareaRef.current?.focus();
  }, [autoFocus]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_HEIGHT)}px`;
  }, [data.inputText]);

  useEffect(() => {
    if (!data.isThinking) {
      textareaRef.current?.focus();
    }
  }, [data.isThinking]);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      actions.handleSendMessage();
    }
  };

  const canSend = data.inputText.trim().length > 0 && !data.isThinking;

  return (
    <div className="navi-composer">
      <ApiSelector />
      <div className="navi-composer__divider" />
      <textarea
        ref={textareaRef}
        className="navi-composer__input"
        value={data.inputText}
        onChange={actions.handleChangeInputText}
        onKeyDown={handleKeyDown}
        placeholder={t(placeholder)}
        disabled={data.isThinking}
        rows={1}
      />
      <button
        type="button"
        className={`navi-composer__send${
          canSend ? ' navi-composer__send--active' : ''
        }`}
        onClick={actions.handleSendMessage}
        disabled={!canSend}
        aria-label="Send"
      >
        <ArrowUp />
      </button>
    </div>
  );
};

export default FloatingComposer;
